import React from 'react';

import { useQuery } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';

import { FormattedMessage } from 'react-intl';
import globalMessages from 'translations/messages';

import { Button, Form, Space } from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import Wizard from 'components/StepsWizard';
import Page from 'components/Page';
import Loading from 'components/Loading';
import SurveySelect from 'components/SurveySelect';

import EditSettings from './EditSettings';
import EditContent from './EditContent';
import EditDistributions from './EditDistributions';

import messages from './messages';
import surveyQuery from './query.gql';
import './edit-survey.less';

const STEPS = {
  SETTINGS: 'settings',
  CONTENT: 'content',
  DISTRIBUTIONS: 'distributions',
};

const stepKeys = Object.values(STEPS);

const EditSurvey = ({ surveyId, step, goTo }) => {
  const { loading, data: { survey } = {} } = useQuery(surveyQuery, {
    variables: { id: surveyId },
    skip: !surveyId,
  });

  const [settingsForm] = Form.useForm();

  const { customer: globalCustomer, loading: customerLoading } = useCustomer({ extraFields: 'alias' });

  if (loading || customerLoading) { return <Loading />; }

  const customer = survey?.customer || globalCustomer;

  const onStepChange = (index) => {
    if (surveyId) {
      const activeKey = stepKeys[index];
      goTo({ step: activeKey });
    }
  };

  const submitStep = async (index) => {
    if (index === stepKeys.indexOf(STEPS.SETTINGS)) {
      await settingsForm.validateFields();
      settingsForm.submit();
      return !!surveyId;
    }

    if (index === stepKeys.indexOf(STEPS.DISTRIBUTIONS)) {
      goTo({ page: 'surveys', id: null, step: null });
      return false;
    }

    return true;
  };

  const onSettingsDone = (id) => {
    if (id) {
      goTo({ step: STEPS.CONTENT });
    }
  };

  const steps = [
    {
      key: STEPS.SETTINGS,
      title: <FormattedMessage {...globalMessages.settings} />,
      render: () => (
        // EditSettings doesn't query it's own data
        // in order to prevent double loading indicator.
        // As it is the default active step.
        <EditSettings
          customer={customer}
          survey={survey}
          form={settingsForm}
          onDone={onSettingsDone}
        />
      ),
    },
    {
      key: STEPS.CONTENT,
      title: <FormattedMessage {...globalMessages.surveyContent} />,
      disabled: !surveyId,
      nextLabel: <FormattedMessage {...globalMessages.next} />,
      render: () => (
        <EditContent customerId={customer.id} surveyId={surveyId} />
      ),
    },
    {
      key: STEPS.DISTRIBUTIONS,
      title: <FormattedMessage {...globalMessages.distributions} />,
      disabled: !surveyId,
      nextLabel: <FormattedMessage {...globalMessages.done} />,
      render: () => (
        <EditDistributions
          customerId={customer.id}
          surveyId={surveyId}
          surveyGroup={survey.languageParent}
          goTo={goTo}
        />
      ),
    },
  ];

  const getInitialStep = () => {
    const key = stepKeys.findIndex(k => k === step);
    return key !== -1 ? key : 0;
  };

  const getHeaderTitle = () => {
    if (!survey) {
      return <FormattedMessage {...messages.createSurvey} />;
    }

    return (
      <Space>
        Edit survey
        <SurveySelect surveyGroupId={survey.languageParent.id} value={survey.id} onChange={(id) => goTo({ surveyId: id })} />
      </Space>
    );
  };

  const getHeaderActions = () => [
    <Button key="translate" type="primary" disabled={!survey} onClick={() => goTo({ step: 'translate' })}>
      <TranslationOutlined />
      <FormattedMessage {...messages.manageTranslations} />
    </Button>,
  ];

  return (
    <Page>
      <Page.Header title={getHeaderTitle()} extra={getHeaderActions()} />
      <Page.Content className="edit-survey">
        <Wizard
          initalKey={getInitialStep()}
          steps={steps}
          onChange={onStepChange}
          onNext={submitStep}
        />
      </Page.Content>
    </Page>
  );
};

export default EditSurvey;
